// Fonctions utilitaires pour les appels API
import { API_URL } from '../config/env';

/**
 * Construit une URL complète à partir d'un chemin relatif
 * @param {string} path - Le chemin relatif (ex: "uploads/products/file.jpg")
 * @returns {string} L'URL complète (ex: "http://192.168.1.70:4000/api/uploads/products/file.jpg")
 */
export const USER_PHOTO_PLACEHOLDER = '/svg/user-placeholder.svg';

export const isPlaceholderUserPhoto = (photo) => {
  if (!photo || typeof photo !== 'string') return true;
  const trimmed = photo.trim();
  return !trimmed || trimmed.includes('user-placeholder');
};

/**
 * URL affichable pour la photo de profil (Google, base64, uploads/…).
 */
export const resolveUserPhotoSrc = (
  photo,
  fallback = USER_PHOTO_PLACEHOLDER
) => {
  if (isPlaceholderUserPhoto(photo)) return fallback;
  return getImageUrl(photo.trim()) || fallback;
};

/**
 * Extrait l'objet utilisateur depuis les réponses API (racine, data ou user).
 */
export const normalizeApiUser = (payload) => {
  if (!payload || typeof payload !== 'object') return null;

  const nested = payload.data;
  if (
    nested &&
    typeof nested === 'object' &&
    (nested.id || nested._id || nested.email)
  ) {
    return nested;
  }

  if (payload.user && typeof payload.user === 'object') {
    return payload.user;
  }

  if (payload.id || payload._id || payload.email) {
    return payload;
  }

  return null;
};

export const getImageUrl = (path) => {
  if (!path) return '';

  // Si l'image provient de google ou est un base64, retourner directement le chemin
  if (path.startsWith('http') || path.startsWith('data:')) {
    return path;
  }

  // Extraire la base URL de API_URL en enlevant "/api/"
  const baseUrl = API_URL.replace(/\/api\/?$/, '').replace(/\/$/, '');
  
  // Supprimer le slash initial du chemin s'il existe
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  
  // Construire l'URL complète : baseUrl + /api/ + chemin
  return `${baseUrl}/api/${cleanPath}`;
};

// Gestionnaire d'erreurs personnalisé
export const handleAPIError = (error) => {
  if (error.response) {
    // Erreur avec réponse du serveur
    const { status, data } = error.response;
    
    switch (status) {
      case 400:
        return 'Requête invalide';
      case 401:
        return 'Non autorisé - Veuillez vous connecter';
      case 403:
        return 'Accès refusé';
      case 404:
        return 'Ressource non trouvée';
      case 422:
        return data?.message || 'Données invalides';
      case 500:
        return 'Erreur serveur - Veuillez réessayer plus tard';
      default:
        return data?.message || 'Une erreur est survenue';
    }
  } else if (error.request) {
    // Erreur de réseau
    return 'Problème de connexion - Vérifiez votre connexion internet';
  } else {
    // Autre erreur
    return error.message || 'Une erreur inattendue s\'est produite';
  }
};

// Validation des données avant envoi
export const validateFormData = (data, rules) => {
  const errors = {};
  
  Object.keys(rules).forEach(field => {
    const rule = rules[field];
    const value = data[field];
    
    // Requis
    if (rule.required && (!value || value.toString().trim() === '')) {
      errors[field] = `${rule.label || field} est requis`;
      return;
    }
    
    // Email
    if (rule.email && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      errors[field] = 'Format d\'email invalide';
      return;
    }
    
    // Longueur minimale
    if (rule.minLength && value && value.toString().length < rule.minLength) {
      errors[field] = `${rule.label || field} doit contenir au moins ${rule.minLength} caractères`;
      return;
    }
    
    // Longueur maximale
    if (rule.maxLength && value && value.toString().length > rule.maxLength) {
      errors[field] = `${rule.label || field} ne doit pas dépasser ${rule.maxLength} caractères`;
      return;
    }
    
    // Nombre minimum
    if (rule.min && value && Number(value) < rule.min) {
      errors[field] = `${rule.label || field} doit être supérieur ou égal à ${rule.min}`;
      return;
    }
    
    // Nombre maximum
    if (rule.max && value && Number(value) > rule.max) {
      errors[field] = `${rule.label || field} doit être inférieur ou égal à ${rule.max}`;
      return;
    }
  });
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Formatage des données pour l'API
export const formatDataForAPI = (data) => {
  const formatted = { ...data };
  
  // Convertir les dates en format ISO
  Object.keys(formatted).forEach(key => {
    if (formatted[key] instanceof Date) {
      formatted[key] = formatted[key].toISOString();
    }
  });
  
  // Supprimer les champs vides
  Object.keys(formatted).forEach(key => {
    if (formatted[key] === '' || formatted[key] === null || formatted[key] === undefined) {
      delete formatted[key];
    }
  });
  
  return formatted;
};

// Construction des paramètres de requête
export const buildQueryParams = (params) => {
  const queryParams = new URLSearchParams();
  
  Object.keys(params).forEach(key => {
    const value = params[key];
    if (value !== null && value !== undefined && value !== '') {
      if (Array.isArray(value)) {
        value.forEach(item => queryParams.append(`${key}[]`, item));
      } else {
        queryParams.append(key, value);
      }
    }
  });
  
  return queryParams.toString();
};

// Gestionnaire de fichiers
export const createFormData = (data) => {
  const formData = new FormData();
  
  Object.keys(data).forEach(key => {
    const value = data[key];
    if (value instanceof File) {
      formData.append(key, value);
    } else if (value instanceof FileList) {
      Array.from(value).forEach(file => {
        formData.append(`${key}[]`, file);
      });
    } else if (value !== null && value !== undefined) {
      formData.append(key, value);
    }
  });
  
  return formData;
};

// Gestionnaire de téléchargement
export const downloadFile = (url, filename) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Fonction de retry pour les requêtes échouées
export const retryAPI = async (apiCall, maxRetries = 3, delay = 1000) => {
  let lastError;
  
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await apiCall();
    } catch (error) {
      lastError = error;
      
      // Ne pas retry pour les erreurs 4xx (sauf 429 - Too Many Requests)
      if (error.response && error.response.status >= 400 && error.response.status < 500 && error.response.status !== 429) {
        throw error;
      }
      
      // Attendre avant de retry
      if (i < maxRetries - 1) {
        await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, i)));
      }
    }
  }
  
  throw lastError;
};

// Cache simple pour les requêtes GET
const cache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const cachedAPI = async (key, apiCall) => {
  const now = Date.now();
  const cached = cache.get(key);
  
  if (cached && (now - cached.timestamp) < CACHE_DURATION) {
    return cached.data;
  }
  
  const result = await apiCall();
  cache.set(key, {
    data: result,
    timestamp: now
  });
  
  return result;
};

// Nettoyer le cache
export const clearCache = (key = null) => {
  if (key) {
    cache.delete(key);
  } else {
    cache.clear();
  }
};
